
import { useState } from "react";
import { Mail, Grid, List } from "lucide-react";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useAvailableLetters } from "@/hooks/useAvailableLetters";
import { useLetterActions } from "@/hooks/useLetterActions";
import { LetterLoadingState } from "@/components/letters/LetterLoadingState";
import { EmptyLetterState } from "@/components/letters/EmptyLetterState";
import { AvailableLettersTable } from "@/components/letters/AvailableLettersTable";
import { AvailableLettersCards } from "@/components/letters/AvailableLettersCards";
import { DeleteLetterDialog } from "@/components/letters/DeleteLetterDialog";
import { Button } from "@/components/ui/button";

export default function AvailableLetters() {
  const { isAdmin, isLoading: isAdminLoading } = useUserProfile();
  const { data: letters, isLoading: isLettersLoading, refetch } = useAvailableLetters(isAdmin, isAdminLoading);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  
  const {
    letterToDelete,
    setLetterToDelete,
    handleView,
    handleShare,
    handleDownload,
    confirmDelete,
    handleDelete
  } = useLetterActions(refetch);

  const isPageLoading = isAdminLoading || isLettersLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cartas Disponíveis</h1>
          <p className="text-gray-600 mt-2">Acesse e compartilhe suas cartas já entregues</p>
        </div>
        
        {letters && letters.length > 0 && (
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('cards')}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      {isPageLoading ? (
        <LetterLoadingState />
      ) : letters && letters.length > 0 ? (
        viewMode === 'cards' ? (
          <AvailableLettersCards
            letters={letters}
            isAdmin={isAdmin}
            onView={handleView}
            onShare={handleShare}
            onDownload={handleDownload}
            onDelete={confirmDelete}
          />
        ) : (
          <AvailableLettersTable
            letters={letters}
            isAdmin={isAdmin}
            onView={handleView}
            onShare={handleShare}
            onDownload={handleDownload}
            onDelete={confirmDelete}
          />
        )
      ) : (
        <EmptyLetterState
          title="Nenhuma carta disponível"
          description="Você ainda não tem cartas disponíveis para visualização. Elas aparecerão aqui quando forem entregues conforme a data agendada."
          icon={<Mail className="h-8 w-8 text-green-500" />}
        />
      )}
      
      <DeleteLetterDialog
        isOpen={!!letterToDelete}
        onClose={() => setLetterToDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
